import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2 } from "lucide-react";
import { SCALE_OPTIONS, formatFrameReadout } from "./frameGeometry";
import type { EditableFrame } from "./FrameMapEditor";

const mapRotation = (rotationDeg: number): 0 | 90 => (Math.round(rotationDeg / 90) % 2 !== 0 ? 90 : 0);

/** Side panel of the plan editor: sheet order (drag to renumber), per-frame
 *  controls for the selected sheet (map rotation, scale, remove).
 *
 *  NB: paper size/orientation (A4 pion/poziom, A3, wstęga) is a property of the
 *  TEMPLATE (set-level), NOT a per-frame toggle — one fixed-size viewport can't
 *  produce both. The per-frame "Obrót mapy" below only twists the MAP CONTENT
 *  inside the fixed sheet (e.g. to align a route), it does not rotate the paper. */
export function SheetListPanel({
  frames,
  selectedUid,
  viewportSizeMm,
  onSelect,
  onReorder,
  onRemove,
  onPatchFrame,
}: {
  frames: EditableFrame[];
  selectedUid: string | null;
  viewportSizeMm: number[] | undefined;
  onSelect: (uid: string) => void;
  onReorder: (uids: string[]) => void;
  onRemove: (uid: string) => void;
  onPatchFrame: (uid: string, patch: Partial<Pick<EditableFrame, "rotation_deg" | "scale_denominator" | "label">>) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ordered = [...frames].sort((a, b) => a.order - b.order);
  const uids = ordered.map((f) => f.uid);

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = uids.indexOf(String(active.id));
    const to = uids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    onReorder(arrayMove(uids, from, to));
  };

  if (ordered.length === 0) {
    return (
      <p className="text-xs text-[var(--color-text-muted)] font-mono uppercase tracking-wider text-center py-8">
        Brak arkuszy — kliknij „Dodaj arkusz" na mapie
      </p>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={uids} strategy={verticalListSortingStrategy}>
        <div className="space-y-1">
          {ordered.map((f) => (
            <SheetRow
              key={f.uid}
              frame={f}
              selected={f.uid === selectedUid}
              viewportSizeMm={viewportSizeMm}
              onSelect={() => onSelect(f.uid)}
              onRemove={() => onRemove(f.uid)}
              onPatch={(patch) => onPatchFrame(f.uid, patch)}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SheetRow({
  frame,
  selected,
  viewportSizeMm,
  onSelect,
  onRemove,
  onPatch,
}: {
  frame: EditableFrame;
  selected: boolean;
  viewportSizeMm: number[] | undefined;
  onSelect: () => void;
  onRemove: () => void;
  onPatch: (patch: Partial<Pick<EditableFrame, "rotation_deg" | "scale_denominator" | "label">>) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: frame.uid,
  });
  const readout = formatFrameReadout(frame, viewportSizeMm);
  const rot = mapRotation(frame.rotation_deg);

  const rotBtn = (value: 0 | 90, label: string) => (
    <button
      type="button"
      onClick={() => onPatch({ rotation_deg: value })}
      className={`flex-1 px-2 py-1 text-[11px] font-mono uppercase tracking-wider transition-colors ${
        rot === value
          ? "bg-[var(--color-primary)] text-white"
          : "text-[var(--color-text-secondary)] hover:text-[var(--color-primary)]"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`border transition-colors ${
        selected
          ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)]"
          : "border-[var(--color-border-muted)] hover:border-[var(--color-border-strong)]"
      } ${isDragging ? "opacity-70" : ""}`}
    >
      <div className="flex items-center gap-1.5 px-2 py-1.5">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab text-[var(--color-border-strong)] hover:text-[var(--color-text-secondary)]"
          title="Przeciągnij, aby zmienić kolejność arkuszy"
        >
          <GripVertical size={14} />
        </button>
        <button type="button" onClick={onSelect} className="flex-1 min-w-0 text-left flex items-center gap-2">
          <span className="w-6 h-6 shrink-0 flex items-center justify-center border border-[var(--color-primary)] text-[var(--color-primary)] font-mono text-[11px] font-bold">
            {frame.order}
          </span>
          <span className="min-w-0">
            <span className="block text-xs font-medium text-[var(--color-text-primary)] truncate">
              {frame.label || `Arkusz ${frame.order}`}
              {frame.parcel_id && (
                <span className="ml-1 text-[10px] font-mono uppercase text-[var(--color-text-muted)]">· działka</span>
              )}
            </span>
            <span className="block text-[10px] font-mono text-[var(--color-text-muted)]">
              {readout}{rot ? " · mapa 90°" : ""}
            </span>
          </span>
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="p-1 text-[var(--color-border-strong)] hover:text-[var(--color-primary)] transition-colors"
          title="Usuń arkusz"
        >
          <Trash2 size={13} />
        </button>
      </div>

      {selected && (
        <div className="border-t border-[var(--color-border-muted)] px-2 py-2 space-y-2">
          {/* Rozmiar arkusza na osi X/Y: papier (cm) : skala : teren (m) */}
          <div className="bg-[var(--color-bg-elevated)] border border-[var(--color-border-muted)] px-2 py-1.5">
            <span className="block text-[9px] font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
              Rozmiar arkusza (X × Y): papier : skala : teren
            </span>
            <span className="block text-[11px] font-mono font-semibold text-[var(--color-text-primary)] mt-0.5">
              {readout}
            </span>
          </div>
          <div>
            <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
              Obrót mapy w arkuszu
            </span>
            <div className="mt-0.5 flex border border-[var(--color-border)] divide-x divide-[var(--color-border)]">
              {rotBtn(0, "0° (N↑)")}
              {rotBtn(90, "90°")}
            </div>
            <span className="block text-[9px] font-mono text-[var(--color-text-muted)] mt-0.5 leading-tight">
              Obraca treść mapy w kadrze; orientacja papieru wynika z szablonu.
            </span>
          </div>
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
              Skala 1:x (nadpisuje skalę zestawu)
            </span>
            <select
              className="input-base mt-0.5 text-xs"
              value={SCALE_OPTIONS.includes(frame.scale_denominator) ? String(frame.scale_denominator) : "current"}
              onChange={(e) => {
                if (e.target.value !== "current") onPatch({ scale_denominator: Number(e.target.value) });
              }}
            >
              {SCALE_OPTIONS.map((s) => (
                <option key={s} value={s}>1:{s}</option>
              ))}
              {!SCALE_OPTIONS.includes(frame.scale_denominator) && (
                <option value="current">1:{frame.scale_denominator}</option>
              )}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
              Nr rys. (etykieta)
            </span>
            <input
              className="input-base mt-0.5 text-xs"
              maxLength={64}
              placeholder={`E-${String(frame.order).padStart(2, "0")}`}
              value={frame.label}
              onChange={(e) => onPatch({ label: e.target.value })}
            />
          </label>
        </div>
      )}
    </div>
  );
}
